import { useEffect, useState } from 'react';
import { fetchDashboardMetrics, DashboardMetrics } from '../services/apiClient';
import { MembersList } from '../components/MembersList';
import { EventsList } from '../components/EventsList';
import { useAuth } from '../hooks/useAuth';

const DEFAULT_METRICS: DashboardMetrics = {
  activeMembers: 0,
  pendingMembers: 0,
  vipMembers: 0,
  familyMembers: 0,
  individualMembers: 0,
  upcomingEvents: 0,
  todayCheckIns: 0,
  drawingPoolSize: 0,
};

export const DashboardPage: React.FC = () => {
  const { user } = useAuth();
  const canViewMembers = user?.role !== 'scanner';
  const [metrics, setMetrics] = useState<DashboardMetrics>(DEFAULT_METRICS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadMetrics = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await fetchDashboardMetrics(user?.role ?? 'viewer');
        setMetrics(data);
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to load metrics';
        setError(message);
        setMetrics(DEFAULT_METRICS);
      } finally {
        setLoading(false);
      }
    };

    loadMetrics();
  }, [user?.role]);

  const CARDS = [
    { title: 'Active Members', value: metrics.activeMembers, detail: 'current roster' },
    { title: 'Pending Payment', value: metrics.pendingMembers, detail: 'needs follow-up' },
    { title: 'VIP', value: metrics.vipMembers, detail: 'premium tier' },
    { title: 'Family', value: metrics.familyMembers, detail: 'family plans' },
    { title: 'Individual', value: metrics.individualMembers, detail: 'individual plans' },
  ];

  return (
    <section id="dashboard" className="page-section">
      <div className="page-header">
        <div>
          <p className="page-eyebrow">Overview</p>
          <h2 className="page-title">Dashboard</h2>
          <p className="page-copy">Operational snapshot modeled after the live club admin experience.</p>
        </div>
        {loading && <p className="status-loading">Loading metrics...</p>}
        {error && <p className="status-error">Error: {error}</p>}
      </div>
      <div className="stats-row">
        {CARDS.map((card) => (
          <article key={card.title} className="stat-card">
            <p className="stat-value">{card.value}</p>
            <p className="stat-label">{card.title}</p>
            <p className="stat-detail">{card.detail}</p>
          </article>
        ))}
      </div>

      <div className="summary-strip">
        <div className="summary-chip">
          <span className="summary-chip-label">Upcoming Events</span>
          <strong>{metrics.upcomingEvents}</strong>
        </div>
        <div className="summary-chip">
          <span className="summary-chip-label">Today Check-Ins</span>
          <strong>{metrics.todayCheckIns}</strong>
        </div>
        <div className="summary-chip">
          <span className="summary-chip-label">Drawing Pool</span>
          <strong>{metrics.drawingPoolSize}</strong>
        </div>
      </div>

      <div className="details-section">
        {canViewMembers ? (
          <MembersList />
        ) : (
          <div className="list-container">
            <h3>Recent Members</h3>
            <p className="empty-state">Not available for scanner role.</p>
          </div>
        )}
        <EventsList />
      </div>
    </section>
  );
};
